import { pageRoutes } from 'constants/routing'
import { SalesforceObjectType } from 'constants/salesforce'
import { UseQueryResult } from 'react-query'
import { AnyAction, Dispatch } from 'redux'
import { setSalesforceDetails } from 'state/booking/actions'
import {
  extractPatients,
  getFilterIdFromSalesforceField,
  salesforceFieldToIdMap,
} from 'utils/salesforce'

const getChangedFields = (prevData: SfLead, lead: SfLead): LeadFields => {
  if (!prevData || !lead) {
    return []
  }

  return (Object.keys(lead) as Array<keyof SfLead>).filter(
    (key) => lead[key] !== prevData[key]
  ) as LeadFields
}

export const transformedLeadData = (
  data: SfLead,
  prevData: SfLead
): SalesForceLeadResults => ({
  lead: data,
  leadPatients: extractPatients(data),
  changedFields: getChangedFields(prevData, data),
})

const getChangedFieldsAccount = (
  prevData: SfAccount,
  sfAccount: SfAccount
): AccountFields => {
  if (!prevData || !sfAccount) {
    return []
  }

  return (Object.keys(sfAccount) as Array<keyof SfAccount>).filter(
    (key) => sfAccount[key] !== prevData[key]
  ) as AccountFields
}

const getChangedFieldsCase = (prevData: SfCase, sfCase: SfCase): CaseFields => {
  if (!prevData || !sfCase) {
    return []
  }

  return (Object.keys(sfCase) as Array<keyof SfCase>).filter(
    (key) => sfCase[key] !== prevData[key]
  ) as CaseFields
}

export const transformedCaseData = (
  data: SfCase,
  prevData: SfCase
): SalesForceCaseResults => ({
  sfAccount: data?.Account,
  sfCase: data,
  changedFields: getChangedFieldsCase(prevData, data),
  changedFieldsAccount: getChangedFieldsAccount(
    prevData?.Account,
    data?.Account
  ),
})

export const handleMessageEvent = (
  event: MessageEvent<string>,
  queryData: UseQueryResult<SalesForceLeadResults | SalesForceCaseResults>,
  dispatch: Dispatch<AnyAction>,
  objectType: SalesforceObjectType,
  isSalesForceB2BOn?: boolean
): void => {
  try {
    const message = JSON.parse(event.data)
    const {
      type,
      leadId: newLeadId,
      caseId: newCaseId,
      sfObjectType = '',
    } = message

    const messageHandlers = isSalesForceB2BOn
      ? {
          updateLeadId: () =>
            (newLeadId || newCaseId || sfObjectType) &&
            dispatch(
              setSalesforceDetails({
                sfId: newLeadId || newCaseId,
                sfObjectType,
              })
            ),
          refetch: () => queryData.refetch(),
        }
      : {
          updateLeadId: () =>
            newLeadId && dispatch(setSalesforceDetails({ leadId: newLeadId })),
          refetch: () => queryData.refetch(),
        }

    messageHandlers[type]?.()
  } catch (error) {
    // console.error('Error handling message event:', error);
  }
}

function hasCoordinates(
  sfObject: SfObject
): sfObject is SfObject & {
  Latitude: number
  Longitude: number
} {
  return (
    sfObject &&
    'Latitude' in sfObject &&
    'Longitude' in sfObject &&
    typeof sfObject.Latitude === 'number' &&
    typeof sfObject.Longitude === 'number'
  )
}

const isSfAccount = (
  sfObject: SfObject
): sfObject is SfObject & { PersonMailingAddress: LeadAddress } => {
  return sfObject && 'PersonMailingAddress' in sfObject
}

function isCaseWithAccount(sfObject: SfObject): sfObject is SfCase {
  return sfObject && 'Account' in sfObject
}

export function getSfAddress(
  sfObject: SfObject,
  locationData?: GoogleLocationResult
): LeadAddress | undefined {
  if (isSfAccount(sfObject)) {
    return {
      city: sfObject.PersonMailingAddress.city,
      country: 'United States',
      geocodeAccuracy: sfObject.PersonMailingAddress.geocodeAccuracy,
      latitude: locationData?.lat,
      longitude: locationData?.lng,
      postalCode: sfObject.PersonMailingAddress.postalCode,
      state: sfObject.PersonMailingAddress.state,
      street: sfObject.PersonMailingAddress.street,
    }
  }

  if (isCaseWithAccount(sfObject)) {
    return {
      city: sfObject.Account.PersonMailingAddress.city,
      country: 'United States',
      geocodeAccuracy: sfObject.Account.PersonMailingAddress.geocodeAccuracy,
      latitude: locationData?.lat,
      longitude: locationData?.lng,
      postalCode: sfObject.Account.PersonMailingAddress.postalCode,
      state: sfObject.Account.PersonMailingAddress.state,
      street: sfObject.Account.PersonMailingAddress.street,
    }
  }

  if (sfObject && 'Address' in sfObject) {
    return sfObject.Address
  }

  return undefined
}

function constructQueryParamFromAddressObject(
  sfObject: SfObject,
  locationData?: GoogleLocationResult
): URLSearchParams {
  const params = new URLSearchParams()

  const sfObjectAddress = getSfAddress(sfObject, locationData)

  if (sfObjectAddress?.state) {
    params.append('state', sfObjectAddress.state)
  }

  if (hasCoordinates(sfObject)) {
    params.append('latitude', `${sfObject.Latitude}`)
    params.append('longitude', `${sfObject.Longitude}`)
  }

  const locationParts = []

  if (sfObjectAddress?.city) {
    locationParts.push(sfObjectAddress.city)
  }

  if (sfObjectAddress?.state) {
    locationParts.push(sfObjectAddress.state)
  }

  if (sfObjectAddress?.postalCode) {
    locationParts.push(sfObjectAddress.postalCode)
  }

  if (locationParts.length > 0) {
    params.append('location', `${locationParts.join(', ')}, USA`)
  }

  return params
}

export function appendSalesforceLeadQuery(
  href: string,
  leadId?: string
): string {
  if (!leadId) {
    return href
  }

  const [pathname, existingQuery = ''] = href.split('?')
  const params = new URLSearchParams(existingQuery)
  params.set('sfId', leadId)
  params.set('sfObjectType', 'Lead')

  return `${pathname}?${params.toString()}`
}

export function constructURLFromLead(lead: SfLead): string {
  const baseUrl = '/app/providers'
  const params = new URLSearchParams({
    sfId: lead?.Id,
    sfObjectType: 'Lead',
  })

  const addressParams = constructQueryParamFromAddressObject(lead)
  addressParams.forEach((value, key) => {
    params.append(key, value)
  })

  if (lead && 'Marketing_Tab_PP__c' in lead && lead.Marketing_Tab_PP__c) {
    params.append('partnershipCode', lead.Marketing_Tab_PP__c)
  }

  if (lead && 'Service_type__c' in lead && lead.Service_type__c) {
    params.append(
      'counselingType[]',
      getFilterIdFromSalesforceField(lead.Service_type__c)
    )
  }

  return `${baseUrl}?${params.toString()}`
}

export function constructURLFromCase(
  caseObj: SfCase,
  sfCaseAddress: LeadAddress
): string {
  const baseUrl = '/app/providers'
  const params = new URLSearchParams({
    sfId: caseObj?.Id,
    sfObjectType: 'case',
  })

  if (sfCaseAddress?.state) {
    params.append('state', sfCaseAddress?.state)
  }

  if (sfCaseAddress?.latitude) {
    params.append('latitude', `${sfCaseAddress.latitude}`)
  }

  if (sfCaseAddress?.longitude) {
    params.append('longitude', `${sfCaseAddress.longitude}`)
  }

  if (sfCaseAddress?.state && sfCaseAddress?.city) {
    params.append(
      'location',
      `${sfCaseAddress?.city}, ${sfCaseAddress?.state}, USA`
    )
  }

  if (
    caseObj.Account &&
    'Marketing_Tab__c' in caseObj.Account &&
    caseObj.Account.Marketing_Tab__c
  ) {
    params.append('partnershipCode', caseObj.Account.Marketing_Tab__c)
  }

  if (
    caseObj.Account &&
    'Service_Type__c' in caseObj.Account &&
    caseObj.Account.Service_Type__c
  ) {
    params.append(
      'counselingType[]',
      getFilterIdFromSalesforceField(caseObj.Account.Service_Type__c)
    )
  } else {
    params.append(
      'counselingType[]',
      salesforceFieldToIdMap['individualcounseling']
    )
  }

  return `${baseUrl}?${params.toString()}`
}

export const getProviderSearchUrl = (
  hasLeadId: boolean,
  lead: SfLead,
  hasCaseId: boolean,
  sfCase: SfCase,
  sfAccountAddress: LeadAddress,
  sfCaseAutoFill?: boolean
): string => {
  if (hasLeadId) {
    return constructURLFromLead(lead)
  } else if (sfCaseAutoFill && hasCaseId) {
    return constructURLFromCase(sfCase, sfAccountAddress)
  }

  return pageRoutes.PROVIDER_SEARCH
}
